import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";
import { SubscriberFilters } from "../types/newsletterEdition";

export const uploadSubscribers = async (
  file: File,
  description?: string,
  emailCount?: number,
  fieldMetadata?: string,
  skipWelcomeEmail?: boolean
) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) {
    formData.append('description', description);
  }
  if (emailCount) {
    formData.append('email_count', emailCount.toString());
  }
  if (fieldMetadata) {
    formData.append('field_metadata', fieldMetadata);
  }
  if (skipWelcomeEmail) {
    formData.append('skip_welcome_email', 'true');
  }

  const response = await axios.post(
    `${getApiUrlWithVersion()}/subscribers`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      }
    },
  );

  return response.data;
};

export const getSubscribers = async (currentPage: number, filters?: SubscriberFilters) => {
  const params: any = { current_page: currentPage };

  // Add filter parameters if provided
  if (filters) {
    if (filters.status && filters.status.length > 0) {
      params.status = filters.status;
    }
    if (filters.dateFrom) {
      params.date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.date_to = filters.dateTo;
    }
    if (filters.search) {
      params.search = filters.search;
    }
  }

  const url = `${getApiUrlWithVersion()}/subscribers`;
    const response = await axios.get(url,
      {
        headers: getAuthHeaders(),
        params,
      }
    );
    return response.data;
};

export const addSubscriber = async (
  email: string,
  customFields?: Record<string, string | number | boolean | null>,
  bypassValidation?: boolean,
  skipWelcomeEmail?: boolean
) => {
  const body: any = { email };
  if (customFields) {
    body.custom_fields = customFields;
  }
  if (bypassValidation) {
    body.bypass_validation = true;
  }
  if (skipWelcomeEmail) {
    body.skip_welcome_email = true;
  }
  const response = await axios.post(
    `${getApiUrlWithVersion()}/subscribers/add`,
    body,
    { headers: getAuthHeaders() }
  );
  return response.data;
}

export const getSubscriberCounts = async (filters?: SubscriberFilters) => {
  const params: any = {};

  // Add filter parameters if provided
  if (filters) {
    if (filters.status && filters.status.length > 0) {
      params.status = filters.status;
    }
    if (filters.dateFrom) {
      params.date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.date_to = filters.dateTo;
    }
    if (filters.search) {
      params.search = filters.search;
    }
  }

  const url = `${getApiUrlWithVersion()}/subscribers/counts`;

    const response = await axios.get(url,
      {
        headers: getAuthHeaders(),
        params
      }
    );
    return response.data;
   
};

export const exportSubscribersCsv = async (filters?: SubscriberFilters): Promise<void> => {
  const params: any = {};

  if (filters) {
    if (filters.status && filters.status.length > 0) params.status = filters.status;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.search) params.search = filters.search;
  }

  const response = await axios.get(
    `${getApiUrlWithVersion()}/subscribers/export`,
    { headers: getAuthHeaders(), params, responseType: "blob" }
  );

  const blob = new Blob([response.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;

  const disposition = response.headers["content-disposition"];
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  a.download = filenameMatch ? filenameMatch[1] : "subscribers.csv";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const suppressEmail = async (email: string) => {
  const response = await axios.patch(
    `${getApiUrlWithVersion()}/subscribers/suppress`,
    { email },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

export const deleteSubscriber = async (email: string) => {
  const response = await axios.delete(
    `${getApiUrlWithVersion()}/subscribers/delete`,
    {
      headers: getAuthHeaders(),
      data: { email }
    }
  );

  return response.data;
};

const getAuthHeaders = () => ({ Authorization: getAuthUser()?.accessToken });
